import {useQuery} from "@apollo/client";
import React from "react";
import {FieldErrors, FormProvider, useForm} from "react-hook-form";

import {NumberInputField} from "@/components/form/fields/NumberInputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {GQL_GET_MSP_CHOICES_GROUPS, GQL_GET_SERVICE_DEFINITIONS, MspChoices} from "@/lib/queries/checks.ts";
import {ServiceDefinition, ServiceFieldDefinition} from "@/lib/types/checks.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {EditCheckSensitivityRetriesAndTimeoutFormValues} from "@/pages/checks/list/actions/bulkSetSensitivityRetriesAndTimeout/BulkSetSRTModal.tsx";

const DEFAULT_SENSITIVITY_VALUE = "2";
const DEFAULT_NUM_RETRIED_VALUE = "2";

export interface BulkSetSensitivityRetriesAndTimeoutFormProps {
  formId: string;
  onSubmit: (data: EditCheckSensitivityRetriesAndTimeoutFormValues) => void;
  onError?: (
    errors: FieldErrors<EditCheckSensitivityRetriesAndTimeoutFormValues>,
    event?: React.BaseSyntheticEvent
  ) => void;
  serverErrors?: ServerErrors;
}

export const BulkSetSRTForm = ({
  formId,
  onSubmit,
  onError,
  serverErrors,
}: BulkSetSensitivityRetriesAndTimeoutFormProps) => {
  const {data} = useQuery<MspChoices>(GQL_GET_MSP_CHOICES_GROUPS);
  const {data: serviceDefinitionsData} = useQuery<{serviceDefinitions: ServiceDefinition[]}>(
    GQL_GET_SERVICE_DEFINITIONS
  );

  let mspSensitivityField: ServiceFieldDefinition | undefined;
  let mspNumRetriesField: ServiceFieldDefinition | undefined;
  let mspThresholdField: ServiceFieldDefinition | undefined;

  serviceDefinitionsData?.serviceDefinitions.forEach((serviceDefinition) => {
    serviceDefinition.fields.forEach((field) => {
      if (field.field === "msp_sensitivity" && !mspSensitivityField) {
        mspSensitivityField = field;
      }
      if (field.field === "msp_num_retries" && !mspNumRetriesField) {
        mspNumRetriesField = field;
      }
      if (field.field === "msp_threshold" && !mspThresholdField) {
        mspThresholdField = field;
      }
    });
  });

  const methods = useForm<EditCheckSensitivityRetriesAndTimeoutFormValues>({
    defaultValues: {
      msp_sensitivity: data?.mspChoices.msp_sensitivity[1].value || DEFAULT_SENSITIVITY_VALUE,
      msp_num_retries: data?.mspChoices.msp_num_retries[1].value || DEFAULT_NUM_RETRIED_VALUE,
      msp_threshold: undefined,
    },
  });
  return (
    <FormProvider {...methods}>
      <form
        id={formId}
        onSubmit={(e) => {
          methods.handleSubmit(onSubmit, onError)(e);
        }}
      >
        {serverErrors?.msp_threshold && <FormErrorAlerts errors={serverErrors?.msp_threshold} />}
        <div className="row mb-4">
          <div className="col">
            Note: The current sensitivity, retry and timeout settings (if specified) on selected checks will be replaced
            with the new settings chosen below.
          </div>
        </div>

        <div className="row">
          <div className="my-1 col-12">
            <SelectField
              name={ServiceField.SENSITIVITY}
              label="Sensitivity"
              options={data?.mspChoices.msp_sensitivity || []}
              tooltip={mspSensitivityField?.title || ""}
              required={false}
              menuPortalTarget={
                (document.querySelector(`div.outlet-container > div.content-container`) as HTMLElement) ?? document.body
              }
              menuPosition={"fixed"}
            />
          </div>
          <div className="my-1 col-12">
            <SelectField
              name={ServiceField.NUM_RETRIES}
              label="Number of retries"
              options={data?.mspChoices.msp_num_retries || []}
              tooltip={mspNumRetriesField?.title || ""}
              required={false}
              menuPortalTarget={
                (document.querySelector(`div.outlet-container > div.content-container`) as HTMLElement) ?? document.body
              }
              menuPosition={"fixed"}
            />
          </div>
          <div className="my-1 col-12">
            <NumberInputField
              name={ServiceField.THRESHOLD}
              label="Timeout (s)"
              tooltip={mspThresholdField?.title || ""}
              required={false}
              placeholder={"no change"}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
